/**
 * View: /admin/precos — administração da Tabela de Preços.
 *
 * Estrutura:
 *  - Header com versão vigente, alteradoPor, vigenteDesde + botão Salvar
 *  - 3 abas: VENDA / BUNDLES / CUSTOS+ESTRUTURA
 *  - Estado em memória; save serializa pra PUT /api/admin/precos
 *
 * Custos internos só carregam pra admin (backend mascara pra outros papéis).
 */

import {
  calcularOnboardingSugerido,
  type Bundle,
  type BundleItemCobrado,
  type BundleItemIncluso,
  type Multiplicadores,
  type PrecosOnboarding,
  type TabelaPrecosCompleta,
  type TabelaPrecosInput,
} from '@infomatic/shared';

import { api, ApiError } from '../../lib/api.js';
import { getUsuarioLogado } from '../../lib/auth.js';
import { fmtDateTime } from '../../lib/formatters.js';
import { escapeHtml, renderLayout } from '../layout.js';

type Aba = 'venda' | 'bundles' | 'custos';
type BundleKey = 'essencial' | 'professional' | 'premium';

interface State {
  tabela: TabelaPrecosCompleta | null;
  aba: Aba;
  dirty: boolean;
  salvando: boolean;
  bundleAtivo: BundleKey;
}

const state: State = {
  tabela: null,
  aba: 'venda',
  dirty: false,
  salvando: false,
  bundleAtivo: 'essencial',
};

export async function renderPrecosAdmin(): Promise<void> {
  const $app = document.getElementById('app');
  if (!$app) return;

  $app.innerHTML = renderLayout({
    title: 'Preços',
    activeNav: 'precos',
    bodyHtml: `
      <div id="precos-root">
        <div style="padding: var(--sp-8); text-align: center; color: var(--color-text-muted)">
          Carregando tabela de preços…
        </div>
      </div>
    `,
  });

  if (!getUsuarioLogado()) {
    renderUnauthenticated();
    return;
  }

  try {
    const { tabela } = await api.admin.precosGet();
    state.tabela = tabela;
    state.dirty = false;
    rerender();
  } catch (err) {
    renderErro(err);
  }
}

function renderUnauthenticated(): void {
  const $root = document.getElementById('precos-root');
  if (!$root) return;
  $root.innerHTML = `
    <section class="card">
      <div class="card-title">Acesso restrito</div>
      <p style="color: var(--color-text-secondary)">
        Faça login com sua conta Microsoft Infomatic para administrar preços.
      </p>
    </section>
  `;
}

function renderErro(err: unknown): void {
  const $root = document.getElementById('precos-root');
  if (!$root) return;
  const msg = err instanceof ApiError ? err.message : String(err);
  $root.innerHTML = `
    <section class="card" style="border-left: 4px solid var(--color-danger, #c33)">
      <div class="card-title">Erro ao carregar tabela de preços</div>
      <p style="color: var(--color-text-secondary)">${escapeHtml(msg)}</p>
    </section>
  `;
}

function rerender(): void {
  const $root = document.getElementById('precos-root');
  if (!$root || !state.tabela) return;

  $root.innerHTML = `
    ${renderHeader(state.tabela)}
    ${renderAbas(state.aba)}
    <section class="card" style="margin-top: var(--sp-4)">
      ${renderAbaContent()}
    </section>
  `;

  bindEvents();
}

// ─── Header ──────────────────────────────────────────────────────────────

function renderHeader(t: TabelaPrecosCompleta): string {
  const badgeDirty = state.dirty
    ? `<span class="badge" style="background:#fff3cd;color:#856404">Alterações não salvas</span>`
    : '';
  const labelBtn = state.salvando
    ? 'Salvando…'
    : state.dirty
      ? 'Publicar nova versão'
      : 'Sem alterações';
  return `
    <div style="display:flex;align-items:center;gap:var(--sp-4);flex-wrap:wrap;margin-bottom:var(--sp-4)">
      <div style="flex:1">
        <h1 style="font-family:var(--font-display);font-size:var(--fs-3xl);margin:0">
          Tabela de Preços
        </h1>
        <div style="display:flex;gap:var(--sp-3);flex-wrap:wrap;margin-top:var(--sp-2);font-size:var(--fs-xs);color:var(--color-text-muted)">
          <span>Versão <strong>${t.versao}</strong></span>
          <span>·</span>
          <span>Vigente desde ${fmtDateTime(t.vigenteDesde)}</span>
          <span>·</span>
          <span>Alterado por <strong>${escapeHtml(t.alteradoPor)}</strong></span>
        </div>
      </div>
      <div style="display:flex;gap:var(--sp-2);align-items:center">
        ${badgeDirty}
        <button type="button" class="btn btn-primary" data-action="salvar" ${state.dirty && !state.salvando ? '' : 'disabled'}>
          ${labelBtn}
        </button>
      </div>
    </div>
  `;
}

// ─── Abas ────────────────────────────────────────────────────────────────

function renderAbas(ativa: Aba): string {
  const aba = (key: Aba, label: string): string => `
    <button type="button" data-aba="${key}" class="aba-btn${ativa === key ? ' aba-ativa' : ''}">
      ${label}
    </button>
  `;
  return `
    <style>
      .aba-bar { display:flex; gap:2px; border-bottom: 2px solid var(--color-border); }
      .aba-btn {
        background: transparent; border: 0; padding: var(--sp-3) var(--sp-4);
        font-size: var(--fs-sm); font-weight: 600; color: var(--color-text-muted);
        cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px;
      }
      .aba-btn:hover { color: var(--color-text); }
      .aba-ativa { color: var(--color-primary); border-bottom-color: var(--color-primary); }
      .precos-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: var(--sp-3);
      }
      .precos-bloco {
        background: var(--color-surface-alt, #fafafa);
        border: 1px solid var(--color-border);
        border-radius: 8px; padding: var(--sp-3);
      }
      .precos-bloco h3 {
        font-size: var(--fs-xs); font-weight: 700; letter-spacing: 1.5px;
        color: var(--color-primary); text-transform: uppercase;
        margin: 0 0 var(--sp-2) 0;
      }
      .precos-linha {
        display: grid; grid-template-columns: 1fr 110px;
        gap: var(--sp-2); align-items: center; padding: 4px 0;
      }
      .precos-linha label { font-size: var(--fs-sm); color: var(--color-text-secondary); }
      .precos-linha input {
        text-align: right; padding: 6px 8px; border-radius: 4px;
        border: 1px solid var(--color-border); font-family: ui-monospace, monospace;
        font-size: var(--fs-sm);
      }
      .bundle-tabs { display: flex; gap: var(--sp-2); margin-bottom: var(--sp-4); }
      .bundle-tab {
        padding: var(--sp-2) var(--sp-3); border-radius: 6px;
        border: 1px solid var(--color-border); background: var(--color-surface);
        cursor: pointer; font-size: var(--fs-sm); font-weight: 600;
      }
      .bundle-tab-ativo {
        background: var(--color-primary); color: white; border-color: var(--color-primary);
      }
      .item-row {
        display: grid; grid-template-columns: 1fr 1fr auto; gap: var(--sp-2);
        align-items: center; padding: 6px 0;
      }
      .item-row.cobrado { grid-template-columns: 1fr 1fr 130px 110px auto auto; }
      .item-row input, .item-row select {
        padding: 6px 8px; border-radius: 4px;
        border: 1px solid var(--color-border); font-size: var(--fs-sm);
      }
      .item-row .btn-remove {
        background: transparent; border: 0; cursor: pointer;
        color: var(--color-danger, #c33); font-size: var(--fs-lg); padding: 0 4px;
      }
    </style>
    <div class="aba-bar">
      ${aba('venda', 'Venda')}
      ${aba('bundles', 'Bundles')}
      ${aba('custos', 'Custos &amp; Estrutura')}
    </div>
  `;
}

function renderAbaContent(): string {
  if (state.aba === 'venda') return renderAbaVenda();
  if (state.aba === 'bundles') return renderAbaBundles();
  return renderAbaCustos();
}

// ─── Aba: VENDA ──────────────────────────────────────────────────────────

function renderAbaVenda(): string {
  if (!state.tabela) return '';
  const v = state.tabela.venda;
  // Acesso defensivo: campos que podem faltar em docs antigos do Cosmos
  const num = (x: unknown): number => (typeof x === 'number' ? x : 0);
  const linha = (path: string, label: string, valor: unknown): string => `
    <div class="precos-linha">
      <label>${escapeHtml(label)}</label>
      <input type="number" step="0.01" min="0" value="${num(valor)}" data-path="venda.${path}" />
    </div>
  `;
  const bloco = (titulo: string, rows: string, extraStyle = ''): string => `
    <div class="precos-bloco"${extraStyle ? ` style="${extraStyle}"` : ''}><h3>${escapeHtml(titulo)}</h3>${rows}</div>
  `;
  const subColuna = (titulo: string, rows: string): string => `
    <div>
      <div style="font-size:11px;letter-spacing:1px;color:var(--color-primary);font-weight:700;margin:0 0 6px 0;text-transform:uppercase">
        ${escapeHtml(titulo)}
      </div>
      ${rows}
    </div>
  `;

  // E-mail Skymail — bloco largo, layout em 3 colunas (IMAP / Exchange / Extras)
  // Ativação/Migração aparece primeiro em Extras (item destacado da operação).
  const imapRows =
    linha('emailImap.5gb', '5GB', v.emailImap?.['5gb']) +
    linha('emailImap.25gb', '25GB', v.emailImap?.['25gb']) +
    linha('emailImap.50gb', '50GB', v.emailImap?.['50gb']) +
    linha('emailImap.100gb', '100GB', v.emailImap?.['100gb']) +
    linha('emailImap.200gb', '200GB', v.emailImap?.['200gb']);
  const exchangeRows =
    linha('emailExchange.50gb', '50GB', v.emailExchange?.['50gb']) +
    linha('emailExchange.100gb', '100GB', v.emailExchange?.['100gb']) +
    linha('emailExchange.200gb', '200GB', v.emailExchange?.['200gb']);
  const extrasRows =
    linha('emailExtras.ativacaoMigracao', 'Ativação / Migração', v.emailExtras?.ativacaoMigracao) +
    linha('emailExtras.smtp2k', 'SMTP 2.000', v.emailExtras?.smtp2k) +
    linha('emailExtras.smtp5k', 'SMTP 5.000', v.emailExtras?.smtp5k) +
    linha('emailExtras.smtp10k', 'SMTP 10.000', v.emailExtras?.smtp10k) +
    linha('emailExtras.smtp50k', 'SMTP 50.000', v.emailExtras?.smtp50k) +
    linha('emailExtras.auditoria30d', 'Auditoria 30d', v.emailExtras?.auditoria30d) +
    linha('emailExtras.infoBox25gb', 'InfoBox 25GB', v.emailExtras?.infoBox25gb) +
    linha('emailExtras.infoBox50gb', 'InfoBox 50GB', v.emailExtras?.infoBox50gb) +
    linha('emailExtras.infoBox100gb', 'InfoBox 100GB', v.emailExtras?.infoBox100gb) +
    linha('emailExtras.backup60d', 'Backup 60d', v.emailExtras?.backup60d);

  const emailSkymailBody = `
    <div style="display:grid;grid-template-columns:repeat(3, minmax(0, 1fr));gap:var(--sp-4)">
      ${subColuna('IMAP', imapRows)}
      ${subColuna('Exchange', exchangeRows)}
      ${subColuna('Extras', extrasRows)}
    </div>
  `;

  return `
    <div class="precos-grid">
      ${bloco(
        'Essencial',
        linha('essencial.dispositivo', 'Estações / unid.', v.essencial?.dispositivo) +
          linha('essencial.servidor', 'Servidor / unid.', v.essencial?.servidor) +
          linha('essencial.usuarioM365', 'Usuário Microsoft 365', v.essencial?.usuarioM365) +
          linha('essencial.horaSuporte', 'Hora suporte', v.essencial?.horaSuporte) +
          linha('essencial.horaAdicional', 'Hora adicional', v.essencial?.horaAdicional),
      )}
      ${bloco(
        'Professional',
        linha('professional.usuario', 'Estações', v.professional?.usuario) +
          linha('professional.servidorComEdr', 'Servidor c/ EDR', v.professional?.servidorComEdr) +
          linha('professional.usuarioM365', 'Usuário Microsoft 365', v.professional?.usuarioM365),
      )}
      ${bloco(
        'Premium',
        linha('premium.equipeInfra3h', 'Equipe Infra (3h)', v.premium?.equipeInfra3h) +
          linha('premium.gestorMensal', 'Gestor mensal', v.premium?.gestorMensal),
      )}
      ${bloco(
        'Backup Nuvem (COVE)',
        linha('backupNuvem.documents', 'Documents', v.backupNuvem?.documents) +
          linha('backupNuvem.m365', 'M365', v.backupNuvem?.m365) +
          linha('backupNuvem.perGb', 'por GB', v.backupNuvem?.perGb) +
          linha('backupNuvem.estacao500gb', 'Estação 500GB', v.backupNuvem?.estacao500gb) +
          linha(
            'backupNuvem.servidorFisico2tb',
            'Servidor Físico 2TB',
            v.backupNuvem?.servidorFisico2tb,
          ) +
          linha(
            'backupNuvem.servidorVirtual2tb',
            'Servidor Virtual 2TB',
            v.backupNuvem?.servidorVirtual2tb,
          ) +
          linha('backupNuvem.testeRestore', 'Teste de Restore', v.backupNuvem?.testeRestore) +
          linha('backupNuvem.draas', 'DRaaS (por backup de servidor)', v.backupNuvem?.draas),
      )}
      ${bloco(
        'Rede Wireless / Switch',
        linha('redeWireless.ap6Ghz', 'AP 6GHz', v.redeWireless?.ap6Ghz) +
          linha('redeWireless.apLongoAlcance', 'AP Longo Alcance', v.redeWireless?.apLongoAlcance) +
          linha('redeWireless.apExterno', 'AP Externo', v.redeWireless?.apExterno) +
          linha('redeWireless.switch8pGiga', 'Switch 8p Giga', v.redeWireless?.switch8pGiga) +
          linha('redeWireless.switch8pPoe', 'Switch 8p PoE', v.redeWireless?.switch8pPoe) +
          linha('redeWireless.switch24pGiga', 'Switch 24p Giga', v.redeWireless?.switch24pGiga) +
          linha('redeWireless.switch24pPoe', 'Switch 24p PoE', v.redeWireless?.switch24pPoe) +
          linha('redeWireless.switch48pGiga', 'Switch 48p Giga', v.redeWireless?.switch48pGiga) +
          linha('redeWireless.switch48pPoe', 'Switch 48p PoE', v.redeWireless?.switch48pPoe),
      )}
      <!-- Bloco "Firewall (mensal)" removido. Mikrotik 5p / Mikrotik Wireless agora vivem
           no HaaS (cessão de uso) com Adendo HaaS v.1.1. Gestão Firewall Cliente foi
           movida pra Segurança (gestão lógica do firewall do cliente, sem HaaS). -->
      ${bloco(
        'Segurança (add-on)',
        linha('seguranca.antivirus', 'AV Gerenciado', v.seguranca?.antivirus) +
          linha('seguranca.edr', 'AV EDR', v.seguranca?.edr) +
          linha(
            'firewall.gestaoFirewallCliente',
            'Gestão Firewall Cliente',
            v.firewall?.gestaoFirewallCliente,
          ),
      )}
      ${bloco(
        'Dispositivos (inventário)',
        linha('dispositivos.impressora', 'Impressora', v.dispositivos?.impressora) +
          linha('dispositivos.nobreak', 'Nobreak', v.dispositivos?.nobreak) +
          linha('dispositivos.firewall', 'Firewall', v.dispositivos?.firewall) +
          linha('dispositivos.roteadorAp', 'Roteador / AP', v.dispositivos?.roteadorAp) +
          linha('dispositivos.switch', 'Switch', v.dispositivos?.switch) +
          // DVR/NVR removido do template MSP — produto comercial separado (projeto-cftv).
          // Schema mantém `dispositivos.dvr` por compat com docs antigos no Cosmos.
          linha('dispositivos.storage', 'Storage (NAS/SAN)', v.dispositivos?.storage) +
          linha('dispositivos.outros', 'Outros', v.dispositivos?.outros),
      )}
      ${renderCardOnboardingDimensional(v.onboarding, state.tabela.multiplicadores)}
      ${renderCardHaas(v.haas)}
      ${renderCardPremiumModulos(v.premiumModulos)}
      ${bloco('E-mail Skymail', emailSkymailBody, 'grid-column: 1 / -1')}
    </div>
  `;
}

// ─── Card HaaS — Hardware como Serviço (Adendo HaaS v.1.1) ──────────────

function renderCardHaas(haas: TabelaPrecosCompleta['venda']['haas'] | undefined): string {
  // Defensivo: docs antigos do Cosmos podem não ter o bloco (schema seta default
  // via .default(precosHaasDefault), mas defesa extra aqui).
  type ItemKey = 'mikrotik' | 'mikrotikWireless' | 'accessPoint' | 'switch' | 'nobreak';
  const itens: Array<{ key: ItemKey; label: string }> = [
    { key: 'mikrotik', label: 'Mikrotik (firewall/roteador)' },
    { key: 'mikrotikWireless', label: 'Mikrotik Wireless' },
    { key: 'accessPoint', label: 'Access Point' },
    { key: 'switch', label: 'Switch' },
    { key: 'nobreak', label: 'Nobreak' },
  ];

  const linhaItem = (
    key: string,
    label: string,
    it:
      | {
          modeloPadrao?: string;
          quantidadePadrao?: number;
          valorMensalPadrao?: number;
          precoVendaPadrao?: number;
          pctHardwarePadrao?: number;
        }
      | undefined,
  ): string => {
    const modelo = it?.modeloPadrao ?? '';
    const qtd = typeof it?.quantidadePadrao === 'number' ? it.quantidadePadrao : 1;
    const mensal = typeof it?.valorMensalPadrao === 'number' ? it.valorMensalPadrao : 0;
    const venda = typeof it?.precoVendaPadrao === 'number' ? it.precoVendaPadrao : 0;
    const pct = typeof it?.pctHardwarePadrao === 'number' ? it.pctHardwarePadrao : 70;
    // Subtotais informativos (qtd × valor unitário) — só visual no admin.
    const subMensal = qtd * mensal;
    const subVenda = qtd * venda;
    return `
      <div class="haas-linha">
        <span class="haas-item-label">${escapeHtml(label)}</span>
        <input type="text" maxlength="200" value="${escapeHtml(modelo)}"
          data-path="venda.haas.${key}.modeloPadrao" placeholder="Modelo padrão" />
        <input type="number" step="1" min="0" max="9999" value="${qtd}"
          data-path="venda.haas.${key}.quantidadePadrao" />
        <input type="number" step="0.01" min="0" value="${mensal}"
          data-path="venda.haas.${key}.valorMensalPadrao" title="Valor mensal POR UNIDADE" />
        <input type="number" step="0.01" min="0" value="${venda}"
          data-path="venda.haas.${key}.precoVendaPadrao" title="Preço de venda POR UNIDADE" />
        <input type="number" step="1" min="0" max="100" value="${pct}"
          data-path="venda.haas.${key}.pctHardwarePadrao" />
        <span class="haas-subtotal" title="qtd × valor unitário">
          R$ ${subMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês<br>
          R$ ${subVenda.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} venda
        </span>
      </div>
    `;
  };

  return `
    <div class="precos-bloco" style="grid-column: 1 / -1">
      <h3>HaaS — Hardware como Serviço</h3>
      <p style="font-size:var(--fs-xs);color:var(--color-text-secondary);margin:0 0 var(--sp-3)">
        Defaults pros itens de cessão de uso (Adendo HaaS v.1.1). Valores POR UNIDADE — quantidade
        multiplica valor mensal e preço de venda. Modelo é texto livre. Snapshot é congelado por
        proposta pra cláusula 9.2 (parcela não amortizada) funcionar mesmo após alterações aqui.
      </p>
      <div class="haas-tabela">
        <div class="haas-header">
          <span>Item</span>
          <span>Modelo padrão</span>
          <span>Qtd</span>
          <span>Valor mensal/un (R$)</span>
          <span>Preço venda/un (R$)</span>
          <span>% HW</span>
          <span>Subtotal</span>
        </div>
        ${itens.map((i) => linhaItem(i.key, i.label, haas?.[i.key])).join('')}
      </div>
    </div>
    <style>
      .haas-tabela { display: flex; flex-direction: column; gap: var(--sp-2); }
      .haas-header, .haas-linha {
        display: grid;
        grid-template-columns: 180px 1fr 60px 130px 130px 70px 140px;
        gap: var(--sp-2);
        align-items: center;
      }
      .haas-header {
        font-size: var(--fs-xs); font-weight: 700; text-transform: uppercase;
        letter-spacing: 0.5px; color: var(--color-text-secondary);
        padding: 0 4px;
      }
      .haas-linha input {
        padding: 6px 8px; border-radius: 4px;
        border: 1px solid var(--color-border); font-size: var(--fs-sm);
      }
      .haas-item-label { font-weight: 600; font-size: var(--fs-sm); }
      .haas-subtotal {
        font-size: 11px; color: var(--color-text-secondary);
        line-height: 1.3; text-align: right;
      }
    </style>
  `;
}

// ─── Card Módulos Premium (Contrato v2026.08, cláusulas 2.5/2.6) ────────

function renderCardPremiumModulos(
  premium: TabelaPrecosCompleta['venda']['premiumModulos'] | undefined,
): string {
  const itens = premium?.itens ?? [];
  const comboPct = typeof premium?.comboPct === 'number' ? premium.comboPct : 10;

  const linhaModulo = (it: NonNullable<typeof premium>['itens'][number], i: number): string => `
    <div class="pm-linha" data-pm-row="${i}">
      <input type="text" maxlength="40" value="${escapeHtml(it.id)}"
        data-pm-field="id" placeholder="kebab-case-id" />
      <input type="text" maxlength="120" value="${escapeHtml(it.nome)}"
        data-pm-field="nome" placeholder="Nome do módulo" />
      <textarea maxlength="500" rows="2" data-pm-field="escopoResumido"
        placeholder="Escopo resumido (2-3 linhas)">${escapeHtml(it.escopoResumido)}</textarea>
      <input type="number" step="0.01" min="0" value="${it.valorHoraPadrao}"
        data-pm-field="valorHoraPadrao" />
      <label class="pm-ativo">
        <input type="checkbox" ${it.ativo ? 'checked' : ''} data-pm-field="ativo" /> ativo
      </label>
      <button type="button" class="btn btn-ghost btn-sm" data-pm-remove="${i}" title="Remover">🗑</button>
    </div>
  `;

  return `
    <div class="precos-bloco" style="grid-column: 1 / -1">
      <h3>Módulos Premium <span class="pm-badge">v2026.08</span></h3>
      <p style="font-size:var(--fs-xs);color:var(--color-text-secondary);margin:0 0 var(--sp-3)">
        Módulos opcionais empilháveis exclusivos do <strong>Premium</strong> (Contrato v.2026.08,
        cláusulas 2.5/2.6). Catálogo extensível. Combo aplicado quando TODOS marcados na proposta.
      </p>
      <div style="font-size:var(--fs-xs);background:rgba(31,78,121,0.05);border-left:3px solid #1F4E79;padding:8px 12px;margin:0 0 var(--sp-3);border-radius:4px;line-height:1.5;">
        <strong style="color:#1F4E79">Regra de horas por estações:</strong>
        até 15 = <strong>1h</strong> ·
        16-30 = <strong>2h</strong> ·
        31-60 = <strong>3h</strong> ·
        61-100 = <strong>4h</strong> ·
        101-150 = <strong>5h</strong> ·
        +150 = <strong>+1h a cada 50</strong>.
        <br>Valor mensal de cada módulo na proposta = R$/hora × horas calculadas.
      </div>
      <div class="pm-tabela">
        <div class="pm-header">
          <span>ID</span><span>Nome</span><span>Escopo resumido</span>
          <span>R$/hora</span><span>Ativo</span><span></span>
        </div>
        ${itens.map(linhaModulo).join('')}
        ${itens.length === 0 ? '<div class="pm-empty">Nenhum módulo cadastrado.</div>' : ''}
      </div>
      <div class="pm-footer">
        <button type="button" class="btn btn-secondary btn-sm" data-pm-add>+ Adicionar módulo</button>
        <label class="pm-combo">
          Desconto combo (todos marcados):
          <input type="number" step="1" min="0" max="100" value="${comboPct}" data-pm-combo />
          <span>%</span>
        </label>
      </div>
    </div>
    <style>
      .pm-badge { font-size:10px;font-weight:600;color:var(--color-primary);background:rgba(31,78,121,0.08);padding:2px 6px;border-radius:4px;margin-left:6px;vertical-align:middle; }
      .pm-tabela { display:flex;flex-direction:column;gap:var(--sp-2); }
      .pm-header, .pm-linha { display:grid;grid-template-columns:140px 180px 1fr 110px 90px 40px;gap:var(--sp-2);align-items:center; }
      .pm-header { font-size:var(--fs-xs);font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--color-text-secondary);padding:0 4px; }
      .pm-linha input, .pm-linha textarea { padding:6px 8px;border-radius:4px;border:1px solid var(--color-border);font-size:var(--fs-sm);font-family:inherit; }
      .pm-linha textarea { resize:vertical;min-height:38px; }
      .pm-ativo { display:flex;align-items:center;gap:4px;font-size:var(--fs-xs);color:var(--color-text-secondary);white-space:nowrap;cursor:pointer; }
      .pm-ativo input[type="checkbox"] { width:16px;height:16px; }
      .pm-empty { padding:var(--sp-3);text-align:center;color:var(--color-text-muted);font-size:var(--fs-sm);font-style:italic; }
      .pm-footer { display:flex;justify-content:space-between;align-items:center;margin-top:var(--sp-3); }
      .pm-combo { display:flex;align-items:center;gap:6px;font-size:var(--fs-sm);color:var(--color-text-secondary); }
      .pm-combo input { width:60px;padding:4px 6px;border-radius:4px;border:1px solid var(--color-border);font-size:var(--fs-sm);text-align:right; }
    </style>
  `;
}

// ─── Card Onboarding dimensional (briefing 2026-05) ──────────────────────

function fmtBRLOnb(n: number): string {
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function renderCardOnboardingDimensional(
  o: PrecosOnboarding | undefined,
  m: Multiplicadores | undefined,
): string {
  const base = o?.baseFixo ?? 400;
  const vEst = o?.valorPorEstacao ?? 10;
  const vSrv = o?.valorPorServidor ?? 100;
  const vFw = o?.valorPorFirewall ?? 100;
  const vRede = o?.valorPorDispositivoRede ?? 5;
  const mSim = m?.simples ?? 1;
  const mMod = m?.moderado ?? 1.1;
  const mCrit = m?.critico ?? 1.2;

  const inputNum = (path: string, label: string, valor: number): string => `
    <div class="precos-linha">
      <label>${escapeHtml(label)}</label>
      <input type="number" step="0.01" min="0" value="${valor}" data-path="venda.${path}" />
    </div>
  `;
  const inputMultRO = (label: string, valor: number): string => `
    <div class="precos-linha">
      <label>${escapeHtml(label)}</label>
      <input type="number" step="0.01" min="1" value="${valor}" disabled
        title="Edite na seção Multiplicadores (aba Custos)" />
    </div>
  `;

  return `
    <div class="precos-bloco" style="grid-column: 1 / -1;">
      <h3>Onboarding (dimensional — parcelado em 6×)</h3>
      <p style="font-size:var(--fs-xs); color:var(--color-text-muted); margin:0 0 var(--sp-3);">
        Fórmula: (Base + Σ componentes) × multiplicador de ambiente.
        Substitui os tiers fixos Express/Standard/Enterprise.
      </p>

      <div style="display:grid; grid-template-columns: 1fr 1fr; gap:var(--sp-4);">
        <div>
          <div style="font-size:11px; letter-spacing:1px; color:var(--color-primary); font-weight:700; margin:0 0 6px 0; text-transform:uppercase;">
            Base fixa
          </div>
          ${inputNum('onboarding.baseFixo', 'Base fixa (kickoff, docs, PSA)', base)}

          <div style="font-size:11px; letter-spacing:1px; color:var(--color-primary); font-weight:700; margin:var(--sp-3) 0 6px 0; text-transform:uppercase;">
            Valores por componente
          </div>
          ${inputNum('onboarding.valorPorEstacao', 'Por estação de trabalho', vEst)}
          ${inputNum('onboarding.valorPorServidor', 'Por servidor', vSrv)}
          ${inputNum('onboarding.valorPorFirewall', 'Por firewall do cliente', vFw)}
          ${inputNum('onboarding.valorPorDispositivoRede', 'Por dispositivo de rede (switch/AP)', vRede)}

          <div style="font-size:11px; letter-spacing:1px; color:var(--color-primary); font-weight:700; margin:var(--sp-3) 0 6px 0; text-transform:uppercase;">
            Multiplicador de ambiente <span style="font-weight:400; text-transform:none; color:var(--color-text-muted);">— reaproveita o do mensal</span>
          </div>
          ${inputMultRO('Simples / Perfeito', mSim)}
          ${inputMultRO('Moderado', mMod)}
          ${inputMultRO('Crítico', mCrit)}
        </div>

        <div>
          <div style="font-size:11px; letter-spacing:1px; color:var(--color-primary); font-weight:700; margin:0 0 6px 0; text-transform:uppercase;">
            Simulador rápido (não persiste)
          </div>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:var(--sp-2);">
            <label style="display:flex; flex-direction:column; gap:2px; font-size:var(--fs-xs);">
              <span>Estações</span>
              <input type="number" id="sim-onb-estacoes" min="0" value="30" />
            </label>
            <label style="display:flex; flex-direction:column; gap:2px; font-size:var(--fs-xs);">
              <span>Servidores</span>
              <input type="number" id="sim-onb-servidores" min="0" value="2" />
            </label>
            <label style="display:flex; flex-direction:column; gap:2px; font-size:var(--fs-xs);">
              <span>Firewall</span>
              <input type="number" id="sim-onb-firewall" min="0" value="1" />
            </label>
            <label style="display:flex; flex-direction:column; gap:2px; font-size:var(--fs-xs);">
              <span>Rede extra</span>
              <input type="number" id="sim-onb-rede" min="0" value="0" />
            </label>
            <label style="display:flex; flex-direction:column; gap:2px; font-size:var(--fs-xs); grid-column: 1/-1;">
              <span>Ambiente</span>
              <select id="sim-onb-perfil">
                <option value="simples">Simples (×${mSim.toFixed(2)})</option>
                <option value="moderado" selected>Moderado (×${mMod.toFixed(2)})</option>
                <option value="critico">Crítico (×${mCrit.toFixed(2)})</option>
              </select>
            </label>
          </div>
          <div id="sim-onb-resultado" style="margin-top:var(--sp-3); padding:var(--sp-3); background:#fff; border:1px solid var(--color-border); border-radius:6px; font-family:var(--font-mono, monospace); font-size:var(--fs-xs); white-space:pre-line; line-height:1.5; min-height:140px;">
            Ajuste os valores acima e clique fora pra recalcular.
          </div>
          <div style="margin-top:var(--sp-2); font-size:var(--fs-xs);">
            <a href="#" id="onb-ver-historico" style="color:var(--color-primary); text-decoration:underline; cursor:pointer;">Ver histórico de alterações</a>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Liga o simulador embutido do card Onboarding. Roda a cada rerender.
 * Não persiste — só usa o estado atual da tabela em memória.
 */
function attachSimuladorOnboarding(): void {
  const ids = [
    'sim-onb-estacoes',
    'sim-onb-servidores',
    'sim-onb-firewall',
    'sim-onb-rede',
    'sim-onb-perfil',
  ];
  const recalc = (): void => {
    if (!state.tabela) return;
    const $res = document.getElementById('sim-onb-resultado');
    if (!$res) return;
    const num = (id: string): number =>
      Number((document.getElementById(id) as HTMLInputElement | null)?.value || 0);
    const perfil = ((document.getElementById('sim-onb-perfil') as HTMLSelectElement | null)
      ?.value || 'moderado') as 'simples' | 'moderado' | 'critico';
    const sug = calcularOnboardingSugerido(state.tabela, {
      estacoes: num('sim-onb-estacoes'),
      servidores: num('sim-onb-servidores'),
      firewalls: num('sim-onb-firewall'),
      dispositivosRede: num('sim-onb-rede'),
      perfil,
    });
    const partes = [`Base: ${fmtBRLOnb(sug.baseFixo)}`];
    for (const l of sug.linhas) {
      partes.push(`${l.qtd} × ${l.label} × ${fmtBRLOnb(l.unitario)} = ${fmtBRLOnb(l.subtotal)}`);
    }
    partes.push('─'.repeat(36));
    partes.push(`Subtotal: ${fmtBRLOnb(sug.subtotalAntesMultiplicador)}`);
    partes.push(
      `Multiplicador ${sug.perfil} (×${sug.multiplicador.toFixed(2)}): ${fmtBRLOnb(sug.total)}`,
    );
    partes.push('─'.repeat(36));
    partes.push(`Onboarding sugerido: ${fmtBRLOnb(sug.total)}`);
    $res.textContent = partes.join('\n');
  };
  ids.forEach((id) => {
    const $el = document.getElementById(id);
    $el?.addEventListener('input', recalc);
    $el?.addEventListener('change', recalc);
  });
  recalc();

  // Link "Ver histórico de alterações" — abre uma modal simples com os audits
  document.getElementById('onb-ver-historico')?.addEventListener('click', (ev) => {
    ev.preventDefault();
    void abrirHistoricoOnboarding();
  });
}

async function abrirHistoricoOnboarding(): Promise<void> {
  try {
    const dados = await api.admin.onboardingLog();
    const linhas = dados.eventos
      .map(
        (e) =>
          `${fmtDateTime(e.timestamp)} — ${escapeHtml(e.usuarioEmail)}\n${e.diffs
            .map((d) => `  ${d.campo}: ${d.anterior} → ${d.novo}`)
            .join('\n')}`,
      )
      .join('\n\n');
    alert(linhas || 'Sem alterações registradas ainda.');
  } catch (err) {
    alert(
      'Não foi possível carregar histórico: ' + (err instanceof Error ? err.message : String(err)),
    );
  }
}

// ─── Aba: BUNDLES ────────────────────────────────────────────────────────

function renderAbaBundles(): string {
  if (!state.tabela) return '';
  const t = state.tabela;
  const tab = (key: BundleKey, label: string): string => `
    <button type="button" class="bundle-tab${state.bundleAtivo === key ? ' bundle-tab-ativo' : ''}" data-bundle-tab="${key}">
      ${label}
    </button>
  `;
  const bundle = t.bundles[state.bundleAtivo];
  return `
    <div class="bundle-tabs">
      ${tab('essencial', 'Essencial')}
      ${tab('professional', 'Professional')}
      ${tab('premium', 'Premium')}
    </div>
    ${renderBundleForm(state.bundleAtivo, bundle)}
  `;
}

function renderBundleForm(key: BundleKey, b: Bundle): string {
  return `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--sp-4);margin-bottom:var(--sp-4)">
      <div>
        <label style="font-size:var(--fs-xs);letter-spacing:1px;color:var(--color-text-muted);font-weight:600">NOME</label>
        <input type="text" value="${escapeHtml(b.nome)}" data-bundle="${key}" data-field="nome"
          style="width:100%;padding:8px;border:1px solid var(--color-border);border-radius:4px;font-size:var(--fs-sm)" />
      </div>
      <div>
        <label style="font-size:var(--fs-xs);letter-spacing:1px;color:var(--color-text-muted);font-weight:600">SLOGAN</label>
        <input type="text" value="${escapeHtml(b.slogan)}" data-bundle="${key}" data-field="slogan"
          style="width:100%;padding:8px;border:1px solid var(--color-border);border-radius:4px;font-size:var(--fs-sm)" />
      </div>
    </div>
    <div style="margin-bottom:var(--sp-4)">
      <label style="font-size:var(--fs-xs);letter-spacing:1px;color:var(--color-text-muted);font-weight:600">DESCRIÇÃO LONGA</label>
      <textarea data-bundle="${key}" data-field="descricao" rows="3"
        style="width:100%;padding:8px;border:1px solid var(--color-border);border-radius:4px;font-size:var(--fs-sm);font-family:inherit">${escapeHtml(b.descricao)}</textarea>
    </div>

    <div class="precos-bloco" style="margin-bottom:var(--sp-3)">
      <h3>Itens Inclusos (sem cobrança adicional)</h3>
      <div id="inclusos-list">
        ${b.inclusos.map((it, i) => renderInclusoRow(key, it, i)).join('')}
      </div>
      <button type="button" data-add-incluso="${key}" class="btn btn-secondary btn-sm" style="margin-top:var(--sp-2)">
        + Adicionar item incluso
      </button>
    </div>

    <div class="precos-bloco">
      <h3>Itens Cobrados por Unidade</h3>
      <div style="font-size:11px;color:var(--color-text-muted);margin-bottom:6px">
        Digite no campo <strong>Label</strong> ou escolha um item da tabela de venda — valor e unidade são preenchidos automaticamente, mas continuam editáveis.
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 130px 110px auto auto;gap:var(--sp-2);font-size:var(--fs-xs);color:var(--color-text-muted);font-weight:600;padding:0 0 4px 0">
        <div>ID</div><div>LABEL</div><div>UNIDADE</div><div>VALOR R$</div><div>MULT.</div><div></div>
      </div>
      <div id="cobrados-list">
        ${b.cobrados.map((it, i) => renderCobradoRow(key, it, i)).join('')}
      </div>
      <button type="button" data-add-cobrado="${key}" class="btn btn-secondary btn-sm" style="margin-top:var(--sp-2)">
        + Adicionar item cobrado
      </button>
    </div>
    ${renderVendaDataList()}
  `;
}

function renderInclusoRow(key: BundleKey, it: BundleItemIncluso, i: number): string {
  return `
    <div class="item-row" data-bundle="${key}" data-incluso="${i}">
      <input type="text" value="${escapeHtml(it.id)}" data-incluso-field="id" placeholder="kebab-case-id" />
      <input type="text" value="${escapeHtml(it.label)}" data-incluso-field="label" placeholder="Label visível" />
      <button type="button" class="btn-remove" data-remove-incluso="${i}" title="Remover">×</button>
    </div>
  `;
}

function renderCobradoRow(key: BundleKey, it: BundleItemCobrado, i: number): string {
  const unidadeOpts = ['dispositivo', 'usuario', 'servidor', 'mes', 'hora', 'unidade']
    .map((u) => `<option value="${u}" ${u === it.unidade ? 'selected' : ''}>${u}</option>`)
    .join('');
  return `
    <div class="item-row cobrado" data-bundle="${key}" data-cobrado="${i}">
      <input type="text" value="${escapeHtml(it.id)}" data-cobrado-field="id" placeholder="id" />
      <input type="text" value="${escapeHtml(it.label)}" data-cobrado-field="label"
        list="venda-itens-list" placeholder="Label (escolha ou digite)" />
      <select data-cobrado-field="unidade">${unidadeOpts}</select>
      <input type="number" step="0.01" min="0" value="${it.valor}" data-cobrado-field="valor" />
      <input type="checkbox" ${it.aplicaMultiplicador ? 'checked' : ''} data-cobrado-field="aplicaMultiplicador" title="Aplica multiplicador de perfil" />
      <button type="button" class="btn-remove" data-remove-cobrado="${i}" title="Remover">×</button>
    </div>
  `;
}

/**
 * Gera <datalist> com todos os itens da tabela de venda — usado como autocomplete
 * nos campos `label` dos bundles cobrados. Selecionar uma opção dispara o
 * onchange que preenche valor/unidade automaticamente (ver bindBundleEvents).
 */
function renderVendaDataList(): string {
  if (!state.tabela) return '';
  const v = state.tabela.venda;
  const n = (x: unknown): number => (typeof x === 'number' ? x : 0);
  type Opt = { label: string; valor: number; unidade: BundleItemCobrado['unidade'] };
  const opts: Opt[] = [
    { label: 'Estações (Essencial)', valor: n(v.essencial?.dispositivo), unidade: 'dispositivo' },
    { label: 'Servidor gerenciado', valor: n(v.essencial?.servidor), unidade: 'servidor' },
    { label: 'Hora de suporte', valor: n(v.essencial?.horaSuporte), unidade: 'hora' },
    { label: 'Hora adicional', valor: n(v.essencial?.horaAdicional), unidade: 'hora' },
    { label: 'Estações (Professional)', valor: n(v.professional?.usuario), unidade: 'usuario' },
    { label: 'Servidor com EDR', valor: n(v.professional?.servidorComEdr), unidade: 'servidor' },
    {
      label: 'Usuário Microsoft 365 (Essencial)',
      valor: n(v.essencial?.usuarioM365),
      unidade: 'usuario',
    },
    {
      label: 'Usuário Microsoft 365 (Professional)',
      valor: n(v.professional?.usuarioM365),
      unidade: 'usuario',
    },
    {
      label: 'Equipe de Infraestrutura (3h/mês)',
      valor: n(v.premium?.equipeInfra3h),
      unidade: 'mes',
    },
    { label: 'Gestor de TI (visita mensal)', valor: n(v.premium?.gestorMensal), unidade: 'mes' },
    { label: 'AV Gerenciado', valor: n(v.seguranca?.antivirus), unidade: 'usuario' },
    { label: 'AV EDR', valor: n(v.seguranca?.edr), unidade: 'usuario' },
    { label: 'Mikrotik 5 portas', valor: n(v.firewall?.mikrotik5p), unidade: 'unidade' },
    { label: 'Mikrotik Wireless', valor: n(v.firewall?.mikrotikWireless), unidade: 'unidade' },
    {
      label: 'Gestão Firewall Cliente',
      valor: n(v.firewall?.gestaoFirewallCliente),
      unidade: 'unidade',
    },
    { label: 'Backup Documents', valor: n(v.backupNuvem?.documents), unidade: 'unidade' },
    { label: 'Backup M365 (por usuário)', valor: n(v.backupNuvem?.m365), unidade: 'usuario' },
    { label: 'Backup por GB', valor: n(v.backupNuvem?.perGb), unidade: 'unidade' },
    { label: 'Backup Estação 500GB', valor: n(v.backupNuvem?.estacao500gb), unidade: 'unidade' },
    {
      label: 'Backup Servidor Físico 2TB',
      valor: n(v.backupNuvem?.servidorFisico2tb),
      unidade: 'servidor',
    },
    {
      label: 'Backup Servidor Virtual 2TB',
      valor: n(v.backupNuvem?.servidorVirtual2tb),
      unidade: 'servidor',
    },
    {
      label: 'Impressora (inventário)',
      valor: n(v.dispositivos?.impressora),
      unidade: 'dispositivo',
    },
    { label: 'Nobreak (inventário)', valor: n(v.dispositivos?.nobreak), unidade: 'dispositivo' },
    { label: 'Firewall (inventário)', valor: n(v.dispositivos?.firewall), unidade: 'dispositivo' },
    {
      label: 'Roteador / AP (inventário)',
      valor: n(v.dispositivos?.roteadorAp),
      unidade: 'dispositivo',
    },
    { label: 'Switch (inventário)', valor: n(v.dispositivos?.switch), unidade: 'dispositivo' },
  ];
  const options = opts
    .map(
      (o) =>
        `<option value="${escapeHtml(o.label)}" data-valor="${o.valor}" data-unidade="${o.unidade}">R$ ${o.valor.toFixed(2)} · ${o.unidade}</option>`,
    )
    .join('');
  return `<datalist id="venda-itens-list">${options}</datalist>`;
}

// ─── Aba: CUSTOS + ESTRUTURA ─────────────────────────────────────────────

function renderAbaCustos(): string {
  if (!state.tabela) return '';
  const t = state.tabela;
  const c = t.custosInternos;
  const m = t.multiplicadores;
  const o = t.onboardingPorPerfil;

  const linha = (path: string, label: string, valor: number, step = '0.01'): string => `
    <div class="precos-linha">
      <label>${escapeHtml(label)}</label>
      <input type="number" step="${step}" min="0" value="${valor}" data-path="${path}" />
    </div>
  `;
  const bloco = (titulo: string, rows: string): string => `
    <div class="precos-bloco"><h3>${escapeHtml(titulo)}</h3>${rows}</div>
  `;

  const isAdminVisto = c.dataKrt !== '' || c.usdBrl > 0;
  const aviso = isAdminVisto
    ? ''
    : `<div style="background:#fff8e6;border:1px solid #e8d5a0;color:#5a4a1f;padding:var(--sp-3);border-radius:6px;margin-bottom:var(--sp-3);font-size:var(--fs-sm)">
        Você não tem permissão para ver os custos internos auditados. Apenas perfis <strong>admin</strong> conseguem editar essa aba.
      </div>`;

  const perfilSelect = (perfil: 'simples' | 'moderado' | 'critico'): string => {
    const v = o[perfil];
    return `
      <div class="precos-linha">
        <label>${perfil.charAt(0).toUpperCase() + perfil.slice(1)}</label>
        <select data-onboarding-perfil="${perfil}"
          style="padding:6px 8px;border:1px solid var(--color-border);border-radius:4px;font-size:var(--fs-sm)">
          ${['sem', 'express', 'standard', 'enterprise']
            .map((opt) => `<option value="${opt}" ${opt === v ? 'selected' : ''}>${opt}</option>`)
            .join('')}
        </select>
      </div>
    `;
  };

  return `
    ${aviso}
    <div class="precos-grid">
      ${bloco(
        'Multiplicador de perfil',
        linha('multiplicadores.simples', 'Simples', m.simples, '0.01') +
          linha('multiplicadores.moderado', 'Moderado', m.moderado, '0.01') +
          linha('multiplicadores.critico', 'Crítico', m.critico, '0.01'),
      )}
      ${bloco(
        'Onboarding default por perfil',
        perfilSelect('simples') + perfilSelect('moderado') + perfilSelect('critico'),
      )}
      ${bloco(
        'Estrutura',
        linha(
          'custosInternos.estrutura.overheadPct',
          'Overhead %',
          c.estrutura.overheadPct,
          '0.1',
        ) +
          linha(
            'custosInternos.estrutura.impostosPct',
            'Impostos %',
            c.estrutura.impostosPct,
            '0.1',
          ) +
          linha(
            'custosInternos.estrutura.contratosAtivos',
            'Contratos ativos',
            c.estrutura.contratosAtivos,
            '1',
          ),
      )}
      ${bloco(
        'KRT (referência)',
        `<div class="precos-linha">
           <label>Data KRT (AAAA-MM)</label>
           <input type="text" value="${escapeHtml(c.dataKrt)}" data-path="custosInternos.dataKrt"
             pattern="\\d{4}-\\d{2}" placeholder="2026-05" />
         </div>` + linha('custosInternos.usdBrl', 'USD/BRL', c.usdBrl, '0.01'),
      )}
      ${bloco(
        'Plataformas / Licenças',
        linha('custosInternos.plataformas.rmm', 'RMM (N-able)', c.plataformas.rmm) +
          linha('custosInternos.plataformas.antivirus', 'Antivírus (AV)', c.plataformas.antivirus) +
          linha('custosInternos.plataformas.edr', 'EDR (por servidor)', c.plataformas.edr) +
          linha(
            'custosInternos.plataformas.mikrotikCapexAmort',
            'Mikrotik CAPEX amort.',
            c.plataformas.mikrotikCapexAmort,
          ),
      )}
      ${bloco(
        'Backup COVE',
        linha('custosInternos.backupCove.servidor', 'Servidor', c.backupCove.servidor) +
          linha('custosInternos.backupCove.vm', 'VM', c.backupCove.vm) +
          linha('custosInternos.backupCove.estacao', 'Estação', c.backupCove.estacao) +
          linha('custosInternos.backupCove.perGb', '/GB', c.backupCove.perGb) +
          linha('custosInternos.backupCove.m365User', 'M365 / user', c.backupCove.m365User),
      )}
      ${bloco(
        'Mão de obra',
        linha(
          'custosInternos.maoDeObra.haloPsaTecnicoMes',
          'HaloPSA / técnico /mês',
          c.maoDeObra.haloPsaTecnicoMes,
        ) +
          linha(
            'custosInternos.maoDeObra.n1CustoHora',
            'N1 custo / hora',
            c.maoDeObra.n1CustoHora,
          ) +
          linha(
            'custosInternos.maoDeObra.n2CustoHora',
            'N2 custo / hora',
            c.maoDeObra.n2CustoHora,
          ) +
          linha(
            'custosInternos.maoDeObra.gestaoTecnicoMes',
            'Gestão / técnico /mês',
            c.maoDeObra.gestaoTecnicoMes,
          ) +
          linha(
            'custosInternos.maoDeObra.comercialContratoMes',
            'Comercial / contrato /mês',
            c.maoDeObra.comercialContratoMes,
          ),
      )}
    </div>
  `;
}

// ─── bindEvents: liga handlers a cada rerender ──────────────────────────

function setByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i] as string;
    if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
    cur = cur[k] as Record<string, unknown>;
  }
  const lastKey = parts[parts.length - 1] as string;
  cur[lastKey] = value;
}

async function salvarHandler(): Promise<void> {
  if (!state.tabela || state.salvando) return;
  state.salvando = true;
  rerender();
  try {
    const { tabela } = await api.admin.precosSave(state.tabela as unknown as TabelaPrecosInput);
    state.tabela = tabela;
    state.dirty = false;
  } catch (err) {
    alert('Erro ao salvar: ' + (err instanceof ApiError ? err.message : String(err)));
  } finally {
    state.salvando = false;
    rerender();
  }
}

/**
 * Marca como dirty E habilita o botão de save imediatamente sem rerender
 * (rerender perderia o foco/cursor do input). Também atualiza o badge.
 */
function marcarDirty(): void {
  state.dirty = true;
  const $btn = document.querySelector<HTMLButtonElement>('[data-action="salvar"]');
  if ($btn && !state.salvando) {
    $btn.disabled = false;
    $btn.textContent = 'Publicar nova versão';
  }
  // Badge "Alterações não salvas" — injeta se ainda não tiver
  const $header = document.querySelector('[data-action="salvar"]')?.parentElement;
  if ($header && !$header.querySelector('.dirty-badge')) {
    const $badge = document.createElement('span');
    $badge.className = 'badge dirty-badge';
    $badge.style.cssText = 'background:#fff3cd;color:#856404';
    $badge.textContent = 'Alterações não salvas';
    $header.insertBefore($badge, $header.firstChild);
  }
}

function bindEvents(): void {
  const $root = document.getElementById('precos-root');
  if (!$root) return;

  // Liga o simulador de onboarding sempre que a aba Venda está montada
  if (state.aba === 'venda') attachSimuladorOnboarding();

  // Abas
  $root.querySelectorAll<HTMLButtonElement>('[data-aba]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.aba = (btn.dataset.aba as Aba) ?? 'venda';
      rerender();
    });
  });

  // Tabs de bundle
  $root.querySelectorAll<HTMLButtonElement>('[data-bundle-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.bundleAtivo = (btn.dataset.bundleTab as BundleKey) ?? 'essencial';
      rerender();
    });
  });

  // Inputs numéricos com data-path → atualiza state.tabela in-place
  $root.querySelectorAll<HTMLInputElement>('input[data-path]').forEach((inp) => {
    inp.addEventListener('input', () => {
      const path = inp.dataset.path;
      if (!path || !state.tabela) return;
      const v = Number(inp.value);
      if (!Number.isFinite(v)) return;
      setByPath(state.tabela as unknown as Record<string, unknown>, path, v);
      marcarDirty();
    });
  });

  // Inputs de texto com data-path (ex: dataKrt no formato AAAA-MM)
  $root.querySelectorAll<HTMLInputElement>('input[type="text"][data-path]').forEach((inp) => {
    inp.addEventListener('input', () => {
      const path = inp.dataset.path;
      if (!path || !state.tabela) return;
      setByPath(state.tabela as unknown as Record<string, unknown>, path, inp.value);
      marcarDirty();
    });
  });

  // Campos nome/slogan/descricao do bundle ativo
  $root
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-bundle][data-field]')
    .forEach((inp) => {
      inp.addEventListener('input', () => {
        const bundleKey = inp.dataset.bundle as BundleKey | undefined;
        const field = inp.dataset.field as 'nome' | 'slogan' | 'descricao' | undefined;
        if (!bundleKey || !field || !state.tabela) return;
        (state.tabela.bundles[bundleKey] as unknown as Record<string, string>)[field] = inp.value;
        marcarDirty();
      });
    });

  // Linhas de itens inclusos: edição inline + remoção
  $root.querySelectorAll<HTMLElement>('[data-incluso]').forEach((row) => {
    const i = Number(row.dataset.incluso);
    const bundleKey = row.dataset.bundle as BundleKey | undefined;
    if (!bundleKey || !Number.isFinite(i)) return;
    row.querySelectorAll<HTMLInputElement>('[data-incluso-field]').forEach((inp) => {
      inp.addEventListener('input', () => {
        const field = inp.dataset.inclusoField as 'id' | 'label' | undefined;
        if (!field || !state.tabela) return;
        const item = state.tabela.bundles[bundleKey].inclusos[i];
        if (item) {
          (item as unknown as Record<string, string>)[field] = inp.value;
          marcarDirty();
        }
      });
    });
    row.querySelectorAll<HTMLButtonElement>('[data-remove-incluso]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!state.tabela) return;
        state.tabela.bundles[bundleKey].inclusos.splice(i, 1);
        marcarDirty();
        rerender();
      });
    });
  });

  // Linhas de itens cobrados: edição inline + remoção + autocomplete via datalist
  $root.querySelectorAll<HTMLElement>('[data-cobrado]').forEach((row) => {
    const i = Number(row.dataset.cobrado);
    const bundleKey = row.dataset.bundle as BundleKey | undefined;
    if (!bundleKey || !Number.isFinite(i)) return;
    row
      .querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-cobrado-field]')
      .forEach((inp) => {
        const handler = (): void => {
          if (!state.tabela) return;
          const item = state.tabela.bundles[bundleKey].cobrados[i];
          if (!item) return;
          const field = inp.dataset.cobradoField as keyof typeof item | undefined;
          if (!field) return;
          if (field === 'valor') {
            item.valor = Number((inp as HTMLInputElement).value) || 0;
          } else if (field === 'aplicaMultiplicador') {
            item.aplicaMultiplicador = (inp as HTMLInputElement).checked;
          } else if (field === 'unidade') {
            item.unidade = inp.value as typeof item.unidade;
          } else if (field === 'label') {
            item.label = inp.value;
            // Se o valor escolhido vem do datalist, auto-preenche valor + unidade
            const $opt = document.querySelector<HTMLOptionElement>(
              `#venda-itens-list option[value="${CSS.escape(inp.value)}"]`,
            );
            if ($opt) {
              const valorEl = row.querySelector<HTMLInputElement>('[data-cobrado-field="valor"]');
              const unidadeEl = row.querySelector<HTMLSelectElement>(
                '[data-cobrado-field="unidade"]',
              );
              if (valorEl && $opt.dataset.valor) {
                item.valor = Number($opt.dataset.valor);
                valorEl.value = String(item.valor);
              }
              if (unidadeEl && $opt.dataset.unidade) {
                item.unidade = $opt.dataset.unidade as typeof item.unidade;
                unidadeEl.value = item.unidade;
              }
            }
          } else if (field === 'id') {
            item.id = inp.value;
          }
          marcarDirty();
        };
        inp.addEventListener('input', handler);
        inp.addEventListener('change', handler);
      });
    row.querySelectorAll<HTMLButtonElement>('[data-remove-cobrado]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!state.tabela) return;
        state.tabela.bundles[bundleKey].cobrados.splice(i, 1);
        marcarDirty();
        rerender();
      });
    });
  });

  // Botões "+ Adicionar item incluso" / "+ Adicionar item cobrado"
  $root.querySelectorAll<HTMLButtonElement>('[data-add-incluso]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const bundleKey = btn.dataset.addIncluso as BundleKey | undefined;
      if (!bundleKey || !state.tabela) return;
      state.tabela.bundles[bundleKey].inclusos.push({ id: 'novo-item', label: 'Novo item' });
      marcarDirty();
      rerender();
    });
  });
  $root.querySelectorAll<HTMLButtonElement>('[data-add-cobrado]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const bundleKey = btn.dataset.addCobrado as BundleKey | undefined;
      if (!bundleKey || !state.tabela) return;
      state.tabela.bundles[bundleKey].cobrados.push({
        id: 'novo-item',
        label: 'Novo item',
        unidade: 'unidade',
        valor: 0,
        aplicaMultiplicador: false,
      });
      marcarDirty();
      rerender();
    });
  });

  // Selects de onboarding default por perfil (aba Custos)
  $root.querySelectorAll<HTMLSelectElement>('[data-onboarding-perfil]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const perfil = sel.dataset.onboardingPerfil as 'simples' | 'moderado' | 'critico' | undefined;
      if (!perfil || !state.tabela) return;
      state.tabela.onboardingPorPerfil[perfil] = sel.value as
        | 'sem'
        | 'express'
        | 'standard'
        | 'enterprise';
      marcarDirty();
    });
  });

  // Botão salvar (procura por data-action)
  const $btn = $root.querySelector<HTMLButtonElement>('[data-action="salvar"]');
  $btn?.addEventListener('click', () => void salvarHandler());
  // ─── Módulos Premium: edição inline + add + remove + comboPct ──────────
  $root.querySelectorAll<HTMLElement>('[data-pm-row]').forEach((row) => {
    const i = Number(row.dataset.pmRow);
    if (!Number.isFinite(i)) return;
    row
      .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-pm-field]')
      .forEach((inp) => {
        const handler = (): void => {
          if (!state.tabela) return;
          const arr = state.tabela.venda.premiumModulos?.itens;
          const item = arr?.[i];
          if (!item) return;
          const field = inp.dataset.pmField;
          if (!field) return;
          if (field === 'valorHoraPadrao') {
            item.valorHoraPadrao = Number((inp as HTMLInputElement).value) || 0;
          } else if (field === 'ativo') {
            item.ativo = (inp as HTMLInputElement).checked;
          } else if (field === 'id' || field === 'nome' || field === 'escopoResumido') {
            (item as unknown as Record<string, string>)[field] = inp.value;
          }
          marcarDirty();
        };
        inp.addEventListener('input', handler);
        inp.addEventListener('change', handler);
      });
  });
  $root.querySelectorAll<HTMLButtonElement>('[data-pm-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!state.tabela) return;
      const i = Number(btn.dataset.pmRemove);
      const arr = state.tabela.venda.premiumModulos?.itens;
      if (!arr || !Number.isFinite(i)) return;
      arr.splice(i, 1);
      marcarDirty();
      rerender();
    });
  });
  $root.querySelector<HTMLButtonElement>('[data-pm-add]')?.addEventListener('click', () => {
    if (!state.tabela) return;
    if (!state.tabela.venda.premiumModulos) {
      state.tabela.venda.premiumModulos = { itens: [], comboPct: 10 };
    }
    const arr = state.tabela.venda.premiumModulos.itens;
    arr.push({
      id: `modulo-${arr.length + 1}`,
      nome: 'Novo módulo',
      escopoResumido: '',
      valorHoraPadrao: 0,
      ativo: true,
    });
    marcarDirty();
    rerender();
  });
  $root.querySelector<HTMLInputElement>('[data-pm-combo]')?.addEventListener('input', (ev) => {
    if (!state.tabela) return;
    if (!state.tabela.venda.premiumModulos) {
      state.tabela.venda.premiumModulos = { itens: [], comboPct: 10 };
    }
    const v = Number((ev.target as HTMLInputElement).value);
    if (Number.isFinite(v)) {
      state.tabela.venda.premiumModulos.comboPct = Math.max(0, Math.min(100, v));
      marcarDirty();
    }
  });
}
